<template>
  <!-- Grid Row using subgrid -->
  <div
    :data-trade-item-id="tradeItem.id"
    class="grid grid-cols-subgrid col-span-full hover:bg-blue-100/60 transition-colors duration-150 ease-in-out [&:not(:last-child)]:border-b"
    v-bind="$attrs"
    :class="
      {
        'bg-gray-100 border-gray-200': depth === 1,
        'bg-gray-200 border-gray-300': depth === 2,
        'bg-gray-300 border-gray-400': depth === 3,
        'bg-gray-400 border-gray-500': depth >= 4
      }
    "
  >
    <!-- Item Column -->
    <div class="items-center px-6 flex py-4">
      <div
        class="flex items-center space-x-3"
      >
        <div
          v-for="i in depth"
          :key="i"
          class="invisible btn-icon btn-icon-default"
          @click.stop
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-400">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 8h16M4 16h16"
    />
</svg>
        </div>

        <div
          v-if="projectUnarchived && !isEditingDisabled"
          class="cursor-grab active:cursor-grabbing btn-icon btn-icon-default"
          :class="`drag-handle-trade-item-${depth}`"
          @click.stop
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-400">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 8h16M4 16h16"
    />
</svg>
        </div>

        <button
          v-if="tradeItem.has_children"
          data-test="open-trade-item"
          type="button"
          class="btn-icon btn-icon-default"
          @click.stop="showChildren = !showChildren"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
            :class="[{'rotate-90': showChildren}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <button
          v-if="projectUnarchived && !isEditingDisabled"
          type="button"
          class="btn btn-icon btn-icon-blue"
          @click.stop="$emit('add')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-blue-600">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>
        </button>

        <button
          v-if="projectUnarchived && !isEditingDisabled"
          type="button"
          class="p-1 rounded-md hover:bg-blue-100 transition-colors duration-200"
          @click.stop="$emit('edit')"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-blue-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>

        <button
          v-if="projectUnarchived && !isEditingDisabled"
          type="button"
          class="p-1 rounded-md hover:bg-red-100 transition-colors duration-200"
          @click.stop="$emit('delete')"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5 text-red-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
        </button>


        <div class="flex items-center space-x-2 min-w-0">
          <span class="font-medium text-gray-900 flex-shrink-0">{{ fullBillItem }}.{{ tradeItem.billNumber }}</span>

          <ProvisionalSumBadge
            v-if="!tradeItem.has_children && tradeItem.is_provisional_sum"
            class="flex-shrink-0"
          />

          <span
            class="text-gray-700 text-wrap"
            :title="tradeItem.name"
          >{{ tradeItem.name }}</span>
        </div>
      </div>
    </div>

    <!-- Unit Column -->
    <div class="flex items-center px-6 py-4 text-sm text-gray-700">
      {{ tradeItem.has_children ? '' : tradeItem.unit }}
    </div>

    <!-- Quantity Column -->
    <div class="flex items-center px-6 py-4 text-sm text-gray-700 text-right">
      {{ tradeItem.has_children ? '' : australianNumber(tradeItem.quantity) }}
    </div>

    <!-- Rate Column -->
    <div class="flex items-center px-6 py-4 text-sm text-gray-700 text-right">
      {{ tradeItem.has_children ? '' : australianCurrency(tradeItem.rate) }}
    </div>

    <!-- Amount Column -->
    <div class="flex items-center px-6 py-4">
      <div class="w-full flex items-center justify-end space-x-2">
        <span
          v-if="tradeItem.has_children"
          class="text-sm font-medium text-gray-900 text-right"
        >
          <div v-if="showWorksLabel">
            <span
              v-if="showAdjustmentsLabel || showProvisionalSumsLabel"
              class="text-gray-600"
            >Works:</span> {{ $tcc(childBudget) }}
          </div>

          <div v-if="showProvisionalSumsLabel">
            <span
              v-if="showAdjustmentsLabel || showWorksLabel"
              class="text-gray-600"
            >Provisional Sums:</span> {{ $tcc(childProvisionalBudget) }}
          </div>

          <div v-if="showAdjustmentsLabel">
            <span
              v-if="showWorksLabel || showProvisionalSumsLabel"
              class="text-gray-600"
            >Adjustments:</span> {{ $tcc(-(childBudget - childAdjustedBudget)) }}
          </div>
        </span>

        <span
          v-else
          class="text-sm font-medium text-gray-900"
        >
          {{ australianCurrency(tradeItem.budget) }}
        </span>

        <AppInfoHover
          v-if="!tradeItem.has_children && tradeItem.budget > 0"
          :offset="[0, 5]"
          :delay="200"
          :use-background="false"
        >
          <template #icon>
            <div
              class="w-3.5 h-3.5 rounded-full"
              :class="[tradeItem.package_trade_items.length === 0 ? 'bg-red-400' : 'bg-green-400']"
            />
          </template>

          <template #default>
            <div class="max-w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-4">
              <div
                v-if="tradeItem.package_trade_items.length === 0"
                class="text-center py-2"
              >
                <div class="w-8 h-8 mx-auto mb-2 rounded-full bg-red-100 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-red-600">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
</svg>
                </div>

                <p class="text-sm font-semibold text-gray-900">
                  {{ $t('HeadContract.BillOfQuantities.TradeRowItem.unassigned') }}
                </p>

                <p class="text-xs text-gray-500 mt-1">
                  {{ $t('HeadContract.BillOfQuantities.TradeRowItem.unassignedDescription') }}
                </p>
              </div>

              <div
                v-else
                class="space-y-3"
              >
                <div class="flex items-center space-x-2 pb-2 border-b border-gray-100">
                  <div class="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3 text-green-600">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>
                  </div>

                  <p class="font-semibold text-gray-900 text-sm">
                    {{ $t('HeadContract.BillOfQuantities.TradeRowItem.assignedToPackages') }}
                  </p>
                </div>

                <ul class="space-y-2">
                  <li
                    v-for="packageTradeItem in tradeItem.package_trade_items"
                    :key="packageTradeItem.id"
                    class="group"
                  >
                    <RouterLink
                      :to="{ name: 'PackagesDashboard', params: { packageId: packageTradeItem.package_id } }"
                      class="block p-3 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50 transition-all duration-200 ease-in-out"
                    >
                      <div class="flex items-center justify-between">
                        <div class="flex-1 min-w-0">
                          <p class="font-medium text-blue-700 group-hover:text-blue-800 truncate">
                            {{ packageTradeItem.package.name }}
                          </p>

                          <div
                            v-if="packageTradeItem.rate"
                            class="flex items-center space-x-2 mt-1"
                          >
                            <span class="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                              {{ australianNumber(packageTradeItem.quantity) }}{{ tradeItem.unit }}
                            </span>

                            <span class="text-xs text-gray-500">at</span>

                            <span class="text-xs font-medium text-gray-700">
                              {{ australianCurrency(packageTradeItem.rate) }}
                            </span>
                          </div>
                        </div>

                        <div class="w-2 h-2 rounded-full bg-green-400 ml-2 flex-shrink-0" />
                      </div>
                    </RouterLink>
                  </li>
                </ul>
              </div>
            </div>
          </template>
        </AppInfoHover>

        <div
          v-else-if="tradeItem.has_children && childBudget > 0"
          class="w-3.5 h-3.5 rounded-full"
          :class="assignedIndicator.color"
          :title="assignedIndicator.title"
        />

        <div
          v-else
          class="w-3.5 h-3.5 rounded-full invisible"
        />
      </div>
    </div>

    <!-- Children Grid Container -->
    <template v-if="tradeItems.length > 0 && showChildren">
      <Sortable
        :list="tradeItems"
        item-key="id"
        class="ignored contents bg-white"
        :class="{ 'opacity-50 pointer-events-none': updateSortAction.is(States.LOADING) }"
        :disabled="!projectUnarchived || disableDraggable || isEditingDisabled"
        :options="{
          handle: `.drag-handle-trade-item-${depth ? depth + 1 : 1}`
        }"
        filter=".ignored"
        @end="changeSortOrder"
        @move.capture="handleMove"
      >
        <template
          v-if="updateSortAction.is(States.LOADING)"
          #header
        >
          <div class="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bottom-1/2">
            <AppSpinner />
          </div>
        </template>

        <template #item="{element}">
          <TradeRowItem
            :key="`${element.id}`"
            :full-bill-item="`${fullBillItem}.${props.tradeItem.billNumber}`"
            :disable-draggable="disableDraggable"
            :is-editing-disabled="isEditingDisabled"
            :project-unarchived="projectUnarchived"
            :trade-item="element"
            :depth="depth ? depth + 1 : 1"
            :expand-to-depth="expandToDepth"
            @edit="$emit('edit', $event?.tradeItem ? { tradeItem: $event.tradeItem } : { tradeItem: element })"
            @delete="$emit('delete', $event?.tradeItem ? { tradeItem: $event.tradeItem } : { tradeItem: element })"
            @add="$emit('add', $event?.tradeItem ? { tradeItem: $event.tradeItem } : { tradeItem: element })"
          />
        </template>
      </Sortable>
    </template>
  </div>
</template>

<script setup lang="ts">
import ProvisionalSumBadge from '@/components/shared/ProvisionalSumBadge.vue';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, australianNumber } from '@/helpers';
import TradeItem from '@/models/TradeItem';
import { useTradeItemsStore } from '@/store/tradeItems';
import { Sortable } from 'sortablejs-vue3';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  tradeItem: TradeItem;
  fullBillItem: string;
  projectUnarchived: boolean;
  isEditingDisabled: boolean;
  disableDraggable: boolean;
  depth?: number;
  expandToDepth?: number;
}>();

defineEmits<{
  (e: 'edit', data?: { tradeItem: TradeItem });
  (e: 'delete', data?: { tradeItem: TradeItem });
  (e: 'add', data?: { tradeItem: TradeItem });
}>();

defineOptions({
  inheritAttrs: false,
});

const tradeItemsStore = useTradeItemsStore();

const tradeItems = computed(() => {
  return tradeItemsStore.models
    .where('parent_id', props.tradeItem.id)
    .with('package_trade_items', (query) => {
      query.with('package');
    })
    .orderBy('sort_order')
    .get();
});

const workItems = computed(() => {
  return tradeItems.value.filter((tradeItem) => {
    return tradeItem.budget > 0 || tradeItem.has_children;
  });
});

const assignedChildItems = computed(() => {
  return tradeItems.value.filter((tradeItem) => {
    return tradeItem.isAssignedToPackage();
  });
});

const showChildren = ref(false);

// Watch expandToDepth to automatically expand/collapse children
watch(() => props.expandToDepth, (newDepth) => {
  if(props.tradeItem.has_children) {
    if(newDepth === undefined) {
      // Collapse when expandToDepth is reset
      showChildren.value = false;
    } else {
      const currentDepth = props.depth ?? 0;

      // Expand if Infinity or current depth is less than expandToDepth
      if(newDepth === Infinity || currentDepth < newDepth) {
        showChildren.value = true;
      } else {
        // Collapse if at or beyond the target depth
        showChildren.value = false;
      }
    }
  }
}, { immediate: true });

const childBudget = computed(() => {
  if(props.tradeItem.has_children) {
    return props.tradeItem.childBudgetExcludingProvisional();
  }

  return 0;
});

const childAdjustedBudget = computed(() => {
  if(props.tradeItem.has_children) {
    return props.tradeItem.childAdjustedBudget();
  }

  return 0;
});

const childProvisionalBudget = computed(() => {
  if(props.tradeItem.has_children) {
    return props.tradeItem.childProvisionalBudget();
  }

  return 0;
});

const updateSortAction = useStoreApiAction(tradeItemsStore.updateSort);

const changeSortOrderForTradeItem = (tradeItem: TradeItem, index: number) => {
  updateSortAction.request(tradeItem.id, {
    sort_order: index,
  });
};

const changeSortOrder = (e) => {
  const item = tradeItems.value.find((tradeItem) => tradeItem.id === parseInt(e.item.dataset.tradeItemId));

  changeSortOrderForTradeItem(item, e.newIndex + 1);
};

const handleMove = (e) => {
  if(e.related && e.related.classList.contains('ignored')) {
    return false;
  }
};

const assignedIndicator = computed(() => {
  if(assignedChildItems.value.length > 0 && assignedChildItems.value.length < workItems.value.length) {
    return {
      title: 'Some trade items have been assigned to a package',
      color: 'bg-orange-400',
    };
  } else if(assignedChildItems.value.length === workItems.value.length) {
    return {
      title: 'All trade items have been assigned to a package',
      color: 'bg-green-400',
    };
  }

  return {
    title: 'No trade items have been assigned to a package',
    color: 'bg-red-400',
  };
});

const showWorksLabel = computed(() => {
  return childBudget.value !== 0 || childProvisionalBudget.value !== 0;
});

const showProvisionalSumsLabel = computed(() => {
  return childProvisionalBudget.value !== 0;
});

const showAdjustmentsLabel = computed(() => {
  return (childBudget.value + childProvisionalBudget.value) - childAdjustedBudget.value !== 0;
});
</script>

<style scoped></style>
